import type { InteractiveVocabularyFragment } from '../../../lesson-builder.graphql.generated';
import {
  AI_DICTIONARY_YEAR_RANGE_3_6,
  AI_DICTIONARY_YEAR_RANGE_K_2,
  LANGUAGE_CODE_EN_US,
  LANGUAGE_CODE_ES_US,
} from 'global/constants';
import customNanoId from 'lib/nanoid/nanoid';
import {
  DictionaryDefinition,
  FormWordsTranslation,
  ImageData,
} from './vocabulary-setting.constants';
import type { Pluck } from 'lib/gql/pluck';

export function getVocabularyIds(
  vocabulary: DictionaryDefinition[] | null
): number[] {
  const ids: number[] = [];
  vocabulary?.forEach(v => {
    if (v.id) {
      ids.push(v.id);
    }
  });
  return ids;
}

export function initialiseDefinitions(
  definitions:
    | Pluck<
        InteractiveVocabularyFragment,
        'vocabularyDefinitions.aiDefinitions.definitions'
      >[]
    | undefined
) {
  return (
    definitions?.map(d => {
      return {
        id: customNanoId(),
        description: d.definition,
        year: d.year,
        language: d.language || LANGUAGE_CODE_EN_US,
      };
    }) || [
      {
        id: customNanoId(),
        description: '',
        year: AI_DICTIONARY_YEAR_RANGE_K_2,
        language: LANGUAGE_CODE_EN_US,
      },
      {
        id: customNanoId(),
        description: '',
        year: AI_DICTIONARY_YEAR_RANGE_3_6,
        language: LANGUAGE_CODE_EN_US,
      },
    ]
  );
}

function getImageData(
  example: Pluck<
    InteractiveVocabularyFragment,
    'vocabularyDefinitions.aiDefinitions.examples'
  >
): ImageData {
  return {
    alt: example.image?.alt || undefined,
    key: example.image?.key,
    fileName: example.image?.fileName,
    cropData: example.image?.cropJson,
    original: example.image?.original || undefined,
    imageUrl: example.image?.responsive?.[0].urls[0].url || undefined,
  };
}

export function initialiseExamples(
  examples:
    | Pluck<
        InteractiveVocabularyFragment,
        'vocabularyDefinitions.aiDefinitions.examples'
      >[]
    | undefined
) {
  return (
    examples?.map(item => ({
      exampleId: item.exampleId || customNanoId(),
      example: item.example,
      image: item.image ? getImageData(item) : undefined,
      language: item.language || LANGUAGE_CODE_EN_US,
    })) || [
      {
        exampleId: customNanoId(),
        example: '',
        image: undefined,
        language: LANGUAGE_CODE_EN_US,
      },
    ]
  );
}

export function initialiseWordsTranslation(
  word: string,
  wordsTranslation:
    | Pluck<
        InteractiveVocabularyFragment,
        'vocabularyDefinitions.aiDefinitions.wordsTranslation'
      >[]
    | undefined
    | null
): FormWordsTranslation[] {
  return (
    wordsTranslation?.map(w => ({
      word: w.word.trim(),
      language: w.language || LANGUAGE_CODE_EN_US,
    })) || [
      {
        word: word.trim(),
        language: LANGUAGE_CODE_EN_US,
      },
    ]
  );
}

export function getLanguageLabel(language: string | null) {
  switch (language) {
    case LANGUAGE_CODE_EN_US:
      return 'English';
    case LANGUAGE_CODE_ES_US:
      return 'Spanish';
    default:
      return LANGUAGE_CODE_EN_US;
  }
}
